import type { ReactElement } from "react";

import { MarketingLayout } from "./sections/Layout";
import { Pricing as PricingSection } from "./sections/Pricing";
import { Comparison } from "./sections/Comparison";
import { Faq, type FaqItem } from "./sections/Faq";
import { FinalCta } from "./sections/FinalCta";

const BILLING_FAQ: FaqItem[] = [
  {
    q: "What is a credit?",
    a: "One credit is roughly one piece of work — swapping a photo, rewriting a section, adding a page. Small tweaks often cost a fraction of a credit; a big rebuild costs several. You're charged for the work that actually happens, not per message.",
  },
  {
    q: "What happens if I run out?",
    a: "Your site stays online and keeps serving visitors — nothing goes dark. You just can't ask for new changes until your next monthly credits land, you buy more, or your daily 5 refresh at midnight.",
  },
  {
    q: "Do unused credits roll over?",
    a: "Monthly credits stay usable for one extra month, so a quiet month isn't wasted. Credits you buy on top of a plan last a year. The 5 free daily credits are use-it-or-lose-it.",
  },
  {
    q: "Can I change how many credits I get?",
    a: "Yes — move up or down the ladder anytime from your billing page. Credits you've already been given keep their original expiry when you switch.",
  },
  {
    q: "How does the 14-day trial work?",
    a: "You enter a card up front (so we know you're a real human, not a bot), but we don't charge until day 15. Cancel anytime before then and you pay nothing.",
  },
  {
    q: "What payment methods do you accept?",
    a: "Cards (Visa, Mastercard, Amex, Discover), plus Apple Pay and Google Pay. Billing runs through Stripe.",
  },
  {
    q: "Can I switch from monthly to annual later?",
    a: "Yes — switch in your account settings. Annual is prorated against the time left on your monthly plan.",
  },
  {
    q: "Is there a refund policy?",
    a: "Cancel within the first 30 days of a paid plan and we'll refund the charge in full, no questions.",
  },
  {
    q: "Do you charge for custom domains?",
    a: "We don't resell domains, but you can connect any domain you already own at no extra cost. SSL is included.",
  },
];

export function Pricing(): ReactElement {
  return (
    <MarketingLayout>
      <section className="mx-auto w-full max-w-6xl px-6 pt-16 pb-4 sm:pt-24">
        <div className="mx-auto max-w-2xl text-center">
          <div className="marketing-kicker text-[#45635e]">Pricing</div>
          <h1 className="marketing-display mt-5 text-5xl leading-[1.02] text-[#12211d] sm:text-6xl">
            Pay for the work.{" "}
            <span className="block text-[#71817d]">Not for a seat.</span>
          </h1>
          <p className="mt-5 text-lg text-muted-foreground">
            Pick how much you expect to change each month. Pay yearly and two
            months are on us — or go month-to-month, no commitment.
          </p>
        </div>
      </section>
      <PricingSection showHeading={false} />
      <Comparison />
      <Faq eyebrow="Billing" heading="Billing questions, answered." items={BILLING_FAQ} />
      <FinalCta />
    </MarketingLayout>
  );
}
